import React, {useCallback, useEffect, useState} from "react";
import TranscriptAutocomplete from "./TranscriptAutocomplete";
import MiRNAAutocomplete from "./MiRNAAutocomplete";
import TranscriptVisualization from "./TranscriptVisualization";
import QueryCard from './QueryCard';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';

const REGION_KEYS = ["5UTR", "CDS", "3UTR"];
const REGION_LABELS = { "5UTR": "5'UTR", "CDS": "CDS", "3UTR": "3'UTR" };

const PRESETS = {
  stringent: {
    minSeed: "6mer",
    transcriptRegions: {"5UTR": false, "CDS": false, "3UTR": true},
    supplementaryPaired: true,
    seedAccessible: 0.15,
    supplementaryAccessible: 0.0,
    conserved: 21,
    maxKd: 1500
  },
  default: {
    minSeed: "6mer",
    transcriptRegions: {"5UTR": false, "CDS": true, "3UTR": true},
    supplementaryPaired: false,
    seedAccessible: 0.15,
    supplementaryAccessible: 0.0,
    conserved: 0,
    maxKd: 1500
  },
  seedInclusive: {
    minSeed: "4mer",
    transcriptRegions: {"5UTR": true, "CDS": true, "3UTR": true},
    supplementaryPaired: false,
    seedAccessible: 0.0,
    supplementaryAccessible: 0.0,
    conserved: 0,
    maxKd: 15000
  },
  seedless: {
    minSeed: "seedless",
    transcriptRegions: {"5UTR": true, "CDS": true, "3UTR": true},
    supplementaryPaired: false,
    seedAccessible: 0.0,
    supplementaryAccessible: 0.0,
    conserved: 0,
    maxKd: 1000000000
  }
};

const STORAGE_KEY = "rimap-risc-form-state";

function safeLoad() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        return JSON.parse(raw) || {};
    } catch (e) {
        return {};
    }
}

function MappingForm() {
    const saved = safeLoad();

    const [gencode, setGencode] = useState(() => saved.gencode ?? 'v46');
    const [transcriptName, setTranscriptName] = useState(() => saved.transcriptName ?? 'SIRT1-201');
    const [miRName, setMiRName] = useState(() => saved.miRName ?? 'miR-34a-5p');
    const [minSeed, setMinSeed] = useState(() => saved.minSeed ?? '6mer');
    const [maxKd, setMaxKd] = useState(() => saved.maxKd ?? 1500);
    const [responseMessage, setResponseMessage] = useState('');
    const [transcriptRegions, setTranscriptRegions] = useState(() => saved.transcriptRegions ?? {"5UTR": false, "CDS": true, "3UTR": true});
    const [supplementaryPaired, setSupplementaryPaired] = useState(() => saved.supplementaryPaired ?? false);
    const [seedAccessible, setSeedAccessible] = useState(() => saved.seedAccessible ?? 0.15);
    const [supplementaryAccessible, setSupplementaryAccessible] = useState(() => saved.supplementaryAccessible ?? 0.0);
    const [conserved, setConserved] = useState(() => saved.conserved ?? 0);
    const [loading, setLoading] = useState(false);
    const [transcriptData, setTranscriptData] = useState(null);
    const [autoSubmit, setAutoSubmit] = useState(false);
    const [activeContext, setActiveContext] = useState(() => saved.activeContext ?? "default");

    const [maxKdStr, setMaxKdStr] = useState(() => String(saved.maxKd ?? 1500));
    const [seedAccessibleStr, setSeedAccessibleStr] = useState(() => String(saved.seedAccessible ?? 0.15));
    const [supplementaryAccessibleStr, setSupplementaryAccessibleStr] = useState(() => String(saved.supplementaryAccessible ?? 0.0));
    const [conservedStr, setConservedStr] = useState(() => String(saved.conserved ?? 0));

    useEffect(() => {
        try {
            const toSave = {
                gencode,
                transcriptName,
                miRName,
                minSeed,
                maxKd,
                transcriptRegions,
                supplementaryPaired,
                seedAccessible,
                supplementaryAccessible,
                conserved,
                activeContext
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        } catch (e) {
            // ignore storage errors
        }
    }, [gencode, transcriptName, miRName, minSeed, maxKd, transcriptRegions, supplementaryPaired, seedAccessible, supplementaryAccessible, conserved, activeContext]);

    const handleRegionToggle = useCallback((key, checked) => {
        setTranscriptRegions(prev => {
            const prevCount = Object.values(prev).filter(Boolean).length;
            if (!checked && prevCount === 1 && prev[key]) {
                return prev;
            }
            return {...prev, [key]: checked};
        });
    }, []);

    const handleSupplementaryToggle = useCallback((e) => {
        setSupplementaryPaired(e.target.checked);
    }, []);

    const applyPreset = useCallback((name) => {
        const p = PRESETS[name];
        if (!p) return;
        setMinSeed(p.minSeed);
        setTranscriptRegions(p.transcriptRegions);
        setSupplementaryPaired(p.supplementaryPaired);
        setSeedAccessible(p.seedAccessible);
        setSeedAccessibleStr(String(p.seedAccessible));
        setSupplementaryAccessible(p.supplementaryAccessible);
        setSupplementaryAccessibleStr(String(p.supplementaryAccessible));
        setConserved(p.conserved);
        setConservedStr(String(p.conserved));
        setMaxKd(p.maxKd);
        setMaxKdStr(String(p.maxKd));
        setAutoSubmit(true);
    }, []);

    const handleContextClick = useCallback((context) => {
        setActiveContext(context);
        applyPreset(context);
    }, [applyPreset]);

    const handleMaxKdChange = (e) => {
        const val = e.target.value;
        setMaxKdStr(val);
        const num = Number(val);
        if (val !== '' && !isNaN(num)) {
            setMaxKd(num);
        }
    };

    const handleMaxKdBlur = () => {
        if (maxKdStr === '' || isNaN(Number(maxKdStr))) {
            setMaxKdStr(String(maxKd));
        } else {
            let num = Number(maxKdStr);
            if (num < 0) num = 0;
            if (num > 1000000000) num = 1000000000;
            setMaxKd(num);
            setMaxKdStr(String(num));
        }
    };

    const handleSeedAccessibleChange = (e) => {
        const val = e.target.value;
        setSeedAccessibleStr(val);
        const num = Number(val);
        if (val !== '' && !isNaN(num)) {
            setSeedAccessible(num);
        }
    };

    const handleSeedAccessibleBlur = () => {
        if (seedAccessibleStr === '' || isNaN(Number(seedAccessibleStr))) {
            setSeedAccessibleStr(String(seedAccessible));
        } else {
            let num = Number(seedAccessibleStr);
            if (num < 0) num = 0;
            if (num > 1) num = 1;
            setSeedAccessible(num);
            setSeedAccessibleStr(String(num));
        }
    };

    const handleSupplementaryAccessibleChange = (e) => {
        const val = e.target.value;
        setSupplementaryAccessibleStr(val);
        const num = Number(val);
        if (val !== '' && !isNaN(num)) {
            setSupplementaryAccessible(num);
        }
    };

    const handleSupplementaryAccessibleBlur = () => {
        if (supplementaryAccessibleStr === '' || isNaN(Number(supplementaryAccessibleStr))) {
            setSupplementaryAccessibleStr(String(supplementaryAccessible));
        } else {
            let num = Number(supplementaryAccessibleStr);
            if (num < 0) num = 0;
            if (num > 1) num = 1;
            setSupplementaryAccessible(num);
            setSupplementaryAccessibleStr(String(num));
        }
    };

    const handleConservedChange = (e) => {
        const val = e.target.value;
        setConservedStr(val);
        const num = Number(val);
        if (val !== '' && !isNaN(num)) {
            setConserved(num);
        }
    };

    const handleConservedBlur = () => {
        if (conservedStr === '' || isNaN(Number(conservedStr))) {
            setConservedStr(String(conserved));
        } else {
            let num = Number(conservedStr);
            if (num < 0) num = 0;
            if (num > 31) num = 31;
            setConserved(num);
            setConservedStr(String(num));
        }
    };

    const handleSubmit = useCallback(async () => {
        setResponseMessage("");
        setTranscriptData(null);
        setLoading(true);
        const requestBody = {
            gencode,
            transcriptName,
            miRName,
            minSeed,
            maxKd,
            UTR5: transcriptRegions["5UTR"],
            CDS: transcriptRegions["CDS"],
            UTR3: transcriptRegions["3UTR"],
            supplementaryPaired,
            seedAccessible,
            supplementaryAccessible,
            conserved,
        };
        const response = await fetch("https://rimap-risc.api.major.iric.ca/api/data", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify(requestBody),
        });
        let responseBody;
        try {
            responseBody = await response.text();
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}, body: ${responseBody}`);
            }
            const data = JSON.parse(responseBody);
            setResponseMessage(data.bindingLandscape);
            setTranscriptData({
                miRNATargetPair: data.miRNATargetPair,
                transcriptRegions: data.transcriptRegions,
                bindingPositions: data.bindingPositions,
                transcriptAccessibility: data.transcriptAccessibility,
            });
        } catch (error) {
            setResponseMessage("An error occurred.");
        } finally {
            setLoading(false);
        }
    }, [gencode, transcriptName, miRName, minSeed, maxKd, transcriptRegions, supplementaryPaired, seedAccessible, supplementaryAccessible, conserved]);

    useEffect(() => {
        if (autoSubmit) {
            const submit = async () => {
                await handleSubmit();
                setAutoSubmit(false);
            };
            submit().catch(() => setAutoSubmit(false));
        }
    }, [autoSubmit, handleSubmit]);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        handleSubmit();
    };

    const handleDownload = () => {
        const blob = new Blob([responseMessage], {type: "text/plain;charset=utf-8"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "bindingLandscape.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const normalizeGene = (id) => {
        if (!id) return null;
        return String(id).replace(/-\d+$/, '');
    };

    const normalizeMirna = (id) => {
        if (!id) return null;
        const s = String(id);
        return s.startsWith('hsa-') ? s : `hsa-${s}`;
    };

    const extractPairs = () => {
        const pairs = transcriptData?.miRNATargetPair;
        if (!pairs) {
            return [{ gene: normalizeGene(transcriptName), mirna: normalizeMirna(miRName) }];
        }
        if (!Array.isArray(pairs)) {
            return [pairs].map(p => {
                const mir = p?.miRNA ?? p?.miR ?? p?.mirna ?? p?.miRName ?? miRName;
                const gene = p?.gene ?? p?.geneSymbol ?? p?.transcript ?? p?.transcriptName ?? transcriptName;
                return { gene: normalizeGene(gene), mirna: normalizeMirna(mir) };
            });
        }
        return pairs.map((p) => {
            const mir = p?.miRNA ?? p?.miR ?? p?.mirna ?? p?.miRName ?? p?.mirnaName ?? miRName;
            const gene = p?.gene ?? p?.geneSymbol ?? p?.transcript ?? p?.transcriptName ?? transcriptName;
            return { gene: normalizeGene(gene), mirna: normalizeMirna(mir) };
        });
    };

    const seedAccessibleValid = Number.isFinite(seedAccessible) && seedAccessible >= 0 && seedAccessible <= 1;
    const supplementaryAccessibleValid = Number.isFinite(supplementaryAccessible) && supplementaryAccessible >= 0 && supplementaryAccessible <= 1;
    const conservedValid = Number.isFinite(conserved) && conserved >= 0 && conserved <= 31;

    return (
        <QueryCard sx={{ my: 4 }}>
            <Typography variant="h4" align="center" sx={{fontWeight: 700, mb: 2}}>
                RIMap-RISC Query
            </Typography>
            <Divider sx={{mb: 3}} />

            <Box sx={{mb: 2, display: 'flex', justifyContent: 'left'}}>
                <TextField
                    label="Gencode version"
                    value={gencode}
                    disabled
                    onChange={e => setGencode(e.target.value)}
                    size="small"
                    sx={{width: '20%'}}
                />
                <Typography variant="body2" sx={{ml: 2, mt: 1.5}}>
                    (only human gencode.v46 is currently supported)
                </Typography>
            </Box>

            <Box sx={{mb: 2, display: 'flex', justifyContent: 'left', gap: 2}}>
                <Box sx={{width: '70%'}}>
                    <TranscriptAutocomplete
                        label="Human transcript (gencode.v46 with variant number. ex: SIRT1-201)"
                        defaultValue={transcriptName}
                        onChange={setTranscriptName}
                    />
                    <MiRNAAutocomplete
                        label="Human microRNA (miRBase nomenclature without hsa prefix. ex: miR-34a-5p)"
                        defaultValue={miRName}
                        onChange={setMiRName}
                    />
                </Box>
            </Box>

            <Divider sx={{mb: 2}}/>

            <Box sx={{mb: 2}}>
                <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center'}}>
                    <Typography variant="body1">Set detection mode :</Typography>
                    <Button
                        variant={activeContext === "stringent" ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => handleContextClick("stringent")}
                    >
                        Stringent
                    </Button>
                    <Button
                        variant={activeContext === "default" ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => handleContextClick("default")}
                    >
                        Default
                    </Button>
                    <Button
                        variant={activeContext === "seedInclusive" ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => handleContextClick("seedInclusive")}
                    >
                        Permissive
                    </Button>
                    {/*
                    <Button
                        variant={activeContext === "seedless" ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => handleContextClick("seedless")}
                    >
                        All inclusive
                    </Button>
                    */}
                </Box>
            </Box>

            <form onSubmit={handleFormSubmit}>
                <Stack direction="row" spacing={4} alignItems="flex-start" justifyContent="flex-start" sx={{mb: 2}}>
                    <Stack spacing={2} sx={{minWidth: "100%"}}>
                        <Stack direction={"row"} spacing={2}>
                            <FormControl size="small" sx={{width: "100%"}}>
                                <InputLabel id="min-seed-label">Minimum seed allowed</InputLabel>
                                <Select
                                    labelId="min-seed-label"
                                    label="Minimum seed allowed"
                                    value={minSeed}
                                    onChange={e => setMinSeed(e.target.value)}
                                    variant="outlined"
                                >
                                    <MenuItem value="7mer">7mer</MenuItem>
                                    <MenuItem value="6mer">6mer</MenuItem>
                                    <MenuItem value="5mer">5mer</MenuItem>
                                    <MenuItem value="4mer">4mer</MenuItem>
                                    <MenuItem value="seedless">seedless</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Maximum Kd threshold (pM)"
                                type="number"
                                value={maxKdStr}
                                onChange={handleMaxKdChange}
                                onBlur={handleMaxKdBlur}
                                size="small"
                                sx={{width: "100%"}}
                                slotProps={{
                                    input: { inputProps: { step: 100 } }
                                }}
                            />
                        </Stack>

                        <Stack direction={"row"} spacing={2}>
                            <TextField
                                label="Minimum seed-complement accessibility"
                                type="number"
                                value={seedAccessibleStr}
                                onChange={handleSeedAccessibleChange}
                                onBlur={handleSeedAccessibleBlur}
                                size="small"
                                sx={{width: "100%"}}
                                slotProps={{
                                    input: { inputProps: { step: 0.01, min: 0, max: 1 } }
                                }}
                                error={!seedAccessibleValid}
                                helperText={!seedAccessibleValid ? 'Value must be between 0 and 1' : ''}
                            />
                            <TextField
                                label="Minimum supplementary-complement accessibility"
                                type="number"
                                value={supplementaryAccessibleStr}
                                onChange={handleSupplementaryAccessibleChange}
                                onBlur={handleSupplementaryAccessibleBlur}
                                size="small"
                                sx={{width: "100%"}}
                                slotProps={{
                                    input: { inputProps: { step: 0.01, min: 0, max: 1 } }
                                }}
                                error={!supplementaryAccessibleValid}
                                helperText={!supplementaryAccessibleValid ? 'Value must be between 0 and 1' : ''}
                            />
                        </Stack>

                        <TextField
                            label="Minimum conservation (across 100 vertebrates)"
                            type="number"
                            value={conservedStr}
                            onChange={handleConservedChange}
                            onBlur={handleConservedBlur}
                            size="small"
                            sx={{width: "100%"}}
                            slotProps={{
                                input: { inputProps: { step: 1, min: 0, max: 31 } }
                            }}
                            error={!conservedValid}
                            helperText={!conservedValid ? 'Value must be between 0 and 31' : ''}
                        />

                        <Box sx={{textAlign: "left", width: "100%"}}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography>Require supplementary pairing :</Typography>
                                <Checkbox
                                    checked={supplementaryPaired}
                                    onChange={handleSupplementaryToggle}
                                    name="Show only interactions with supplementary pairing"
                                />
                            </Stack>
                        </Box>

                        <Box sx={{mb: 2}}>
                            <Typography variant="body1">Regions to analyze</Typography>
                            <Box>
                                {REGION_KEYS.map((key) => (
                                    <FormControlLabel
                                        key={key}
                                        control={
                                            <Checkbox
                                                checked={transcriptRegions[key]}
                                                onChange={(e) => handleRegionToggle(key, e.target.checked)}
                                            />
                                        }
                                        label={REGION_LABELS[key]}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Stack>
                </Stack>

                <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        type="submit"
                        sx={{width: "50%"}}
                    >
                        MAP/RUN
                    </Button>
                </Box>
            </form>

            {(loading || responseMessage) && (
                <>
                    <Alert severity="info" icon={false} sx={{ mt: 3, p: 0 }}>
                        <Box
                            component="pre"
                            sx={{
                                m: 0,
                                p: 2,
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                whiteSpace: 'pre',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                maxWidth: '100%'
                            }}
                        >
                            {loading ? (
                                'Performing calculations...\n\nThis may take up to 5 minutes, but usually completes in under two.\nThank you for your query, you\'re helping to populate RIMap-RISC!'
                            ) : (
                                responseMessage
                            )}
                        </Box>
                    </Alert>
                    {!loading && responseMessage && (
                        <Box sx={{ mt: 1 }}>
                            <Button variant="outlined" size="small" onClick={handleDownload}>
                                Download interactions
                            </Button>
                        </Box>
                    )}

                    <Box sx={{mt: 2}}>
                        {(() => {
                            const pair = extractPairs().find(p => p && p.gene && p.mirna);
                            if (!pair) return null;
                            const url = `https://dianalab.e-ce.uth.gr/tarbasev9/interactions?gene=${encodeURIComponent(pair.gene)}&mirna=${encodeURIComponent(pair.mirna)}`;
                            const display = `${pair.gene} - ${pair.mirna.replace(/^hsa-/, '')}`;
                            return (
                                <Typography variant="body1">
                                    See transcript-miRNA pair in TarBase-v9.0 :{' '}
                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                        {display}
                                    </a>
                                </Typography>
                            );
                        })()}
                    </Box>
                    
                 </>
             )}

            {transcriptData && (
                <Box sx={{mt: 4}}>
                    <TranscriptVisualization
                        miRNATargetPair={transcriptData.miRNATargetPair}
                        transcriptRegions={transcriptData.transcriptRegions}
                        bindingPositions={transcriptData.bindingPositions}
                        transcriptAccessibility={transcriptData.transcriptAccessibility}
                    />
                </Box>
            )}
        </QueryCard>
    );
}


export default MappingForm;
